/**
 * The plaintext-cache guard (encryption spec section 6.7, decision 28; #1548).
 *
 * The `courses` store keeps localStorage read-caches of notes, highlights,
 * personal tags and blank answers so the Hub and the reader paint before the
 * API answers. The read path (#1542) opens envelopes at the transport seam, so
 * for an enrolled member what the store holds, and what those caches would
 * write, is plaintext. The corpus that replaces the caches with envelopes is
 * part 2 of #1548; until it lands, this guard tells the store when the member
 * is one whose content must not be cached in the clear, and the store's
 * writers stand down and sweep what they wrote before.
 *
 * "Encrypted member" is read from the same three places the sealing hook reads:
 * this device holds the master key, the API's status says enrolled, or an
 * enrolment attempt recorded enrolled or pending. It follows the signed-in
 * member: the logout wipe clears all three, so the next member on a shared
 * phone gets plaintext caches back if they are a plaintext member.
 */

const ENROLLED_STATUSES = Object.freeze(['enrolled', 'pending']);

/**
 * @param {object} store the root Vuex store
 * @returns {boolean}
 */
export function isEncryptedMember(store) {
  if (store.getters['courses-keys/hasMasterKey'] === true) return true;
  if (store.getters['courses-devices/enrolled'] === true) return true;
  return ENROLLED_STATUSES.includes(store.state['courses-keys']?.enrolmentStatus);
}

/**
 * Keep `courses.encryptedMember` in step with the member. Applied once at
 * install, then on every change. Installed at start-up (App.vue) next to the
 * sealing hook, so a reload has it in place before the first fetch.
 *
 * @param {{ store: object }} args
 * @returns {() => void} stops watching
 */
export function installPlaintextCacheGuard({ store }) {
  if (!store) throw new TypeError('installPlaintextCacheGuard needs the root Vuex store');
  const apply = encrypted => store.commit('courses/setEncryptedMember', encrypted);
  apply(isEncryptedMember(store));
  return store.watch(() => isEncryptedMember(store), apply);
}
